import React from 'react';
import { Popover } from 'app/components/common';
import { OverlayTriggerType } from 'react-bootstrap/esm/OverlayTrigger';
import { Currency, formatMoney } from '../../../common/format';
import { CoverageCostsBreakdown, FiscalContributionsBreakdown } from '../types';
import { ProjectionColumn, renderProjectionCell } from './projection-columns';

type RowKeyable = Record<string, number | undefined>;

interface ProjectionTableProps<TRow extends RowKeyable> {
  rows: TRow[];
  columns: ProjectionColumn<TRow>[];
  currency: Currency;
  ufValue: number;
  title?: string;
  maxHeight?: number;
  rowKey?: keyof TRow & string;
}

const COVERAGE_COST_LABELS: Record<string, string> = {
  death: 'Muerte',
  accidental_death: 'Muerte accidental',
  itp: 'ITP',
  serious_illness: 'Enfermedades graves',
};

const FISCAL_CONTRIBUTIONS_LABELS: Record<string, string> = {
  agreed_deposits: 'Depósitos convenidos',
  product_benefit: 'Beneficio del producto',
};

const FISCAL_CONTRIBUTION_FIELD_LABELS: Record<string, string> = {
  monthly_benefit: 'Beneficio en este mes',
  amount_considered_for_benefit: 'Considerado para beneficio',
  accumulated_benefit: 'Acumulado',
  accumulated_interest: 'Interés Acumulado',
};

function getBreakdownLabel(labels: Record<string, string>, key: string): string {
  return labels[key] ?? key.replace(/_/g, ' ');
}

function getOrderedKeys(
  labels: Record<string, string>,
  breakdown: Record<string, unknown>,
): string[] {
  const order = Object.keys(labels);
  return [
    ...order.filter((key) => key in breakdown),
    ...Object.keys(breakdown).filter((key) => !order.includes(key)),
  ];
}

function hasNonZeroValue(breakdown: Record<string, number | undefined>, keys: string[]): boolean {
  return keys.some((key) => (breakdown[key] ?? 0) !== 0);
}

function renderBreakdownPopover(
  formattedValue: string,
  popoverId: string,
  trigger: OverlayTriggerType[],
  width: number,
  renderContent: () => JSX.Element,
) {
  return (
    <Popover
      id={popoverId}
      trigger={trigger}
      placement="auto"
      width={width}
      renderTrigger={() => (
        <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
          {formattedValue}
        </span>
      )}
      renderContent={renderContent}
    />
  );
}

function renderBreakdownCell(
  formattedValue: string,
  breakdown: Record<string, number> | undefined,
  labels: Record<string, string>,
  popoverId: string,
  currency: Currency,
  ufValue: number,
  trigger: OverlayTriggerType[],
) {
  const keys = getOrderedKeys(labels, breakdown ?? {});
  if (keys.length === 0 || !hasNonZeroValue(breakdown ?? {}, keys)) return formattedValue;

  return renderBreakdownPopover(formattedValue, popoverId, trigger, 280, () => (
    <ul className="list-unstyled text-white mb-0 p-3 d-flex flex-column gap-2">
      {keys.map((key) => (
        <li key={key} className="d-flex justify-content-between align-items-baseline gap-3">
          <span className="text-truncate">{getBreakdownLabel(labels, key)}</span>
          <span className="flex-shrink-0 text-end">
            {formatMoney((breakdown ?? {})[key] ?? 0, currency, ufValue)}
          </span>
        </li>
      ))}
    </ul>
  ));
}

function renderFiscalContributionsCell(
  formattedValue: string,
  breakdown: FiscalContributionsBreakdown | undefined,
  popoverId: string,
  currency: Currency,
  ufValue: number,
  trigger: OverlayTriggerType[],
) {
  const categories = getOrderedKeys(FISCAL_CONTRIBUTIONS_LABELS, breakdown ?? {})
    .map((categoryKey) => {
      const detail = (breakdown ?? {})[categoryKey];
      return {
        categoryKey,
        detail,
        fieldKeys: getOrderedKeys(FISCAL_CONTRIBUTION_FIELD_LABELS, detail),
      };
    })
    .filter(({ detail, fieldKeys }) => hasNonZeroValue(detail, fieldKeys));

  if (categories.length === 0) return formattedValue;

  return renderBreakdownPopover(formattedValue, popoverId, trigger, 320, () => (
    <div className="text-white p-3 d-flex flex-column gap-3">
      {categories.map(({ categoryKey, detail, fieldKeys }) => (
        <div key={categoryKey}>
          <div className="fw-semibold mb-1">
            {getBreakdownLabel(FISCAL_CONTRIBUTIONS_LABELS, categoryKey)}
          </div>
          <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
            {fieldKeys.map((fieldKey) => (
              <li key={fieldKey} className="d-flex justify-content-between align-items-baseline gap-3">
                <span className="text-truncate">
                  {getBreakdownLabel(FISCAL_CONTRIBUTION_FIELD_LABELS, fieldKey)}
                </span>
                <span className="flex-shrink-0 text-end">
                  {formatMoney(detail[fieldKey] ?? 0, currency, ufValue)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  ));
}

export function ProjectionTable<TRow extends RowKeyable>({
  rows,
  columns,
  currency,
  ufValue,
  title = 'Detalle anual',
  maxHeight = 480,
  rowKey = 'year',
}: ProjectionTableProps<TRow>) {
  return (
    <div className="card">
      <div className="card-header">
        <strong>{title}</strong>
      </div>
      <div className="table-responsive" style={{ maxHeight }}>
        <table className="table table-sm table-striped mb-0">
          <thead className="sticky-top bg-light">
            <tr>
              {columns.map((col) => (
                <th key={col.id} scope="col" className="small">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const rowIdentifier = (row[rowKey] as number | undefined) ?? rowIndex;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIdentifier}>
                  {columns.map((col) => {
                    const formatted = renderProjectionCell(row, col, currency, ufValue);
                    let content: React.ReactNode = formatted;
                    if (col.id === 'coverage_costs') {
                      content = renderBreakdownCell(
                        formatted,
                        (row as Record<string, unknown>)
                          .coverage_costs_breakdown as CoverageCostsBreakdown | undefined,
                        COVERAGE_COST_LABELS,
                        `coverage-costs-popover-${rowIdentifier}`,
                        currency,
                        ufValue,
                        ['hover', 'focus'],
                      );
                    } else if (col.id === 'fiscal_contributions') {
                      const breakdown = (row as Record<string, unknown>)
                        .fiscal_contributions_breakdown as FiscalContributionsBreakdown | undefined;
                      content = renderFiscalContributionsCell(
                        formatted,
                        breakdown,
                        `fiscal-contributions-popover-${rowIdentifier}`,
                        currency,
                        ufValue,
                        ['hover', 'focus'],
                      );
                    }
                    return (
                      <td key={col.id} className="small">
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
